import React from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error }>;
}

export class ShadowEmbedErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    console.error('ShadowEmbed Error Boundary caught error:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ShadowEmbed Error Boundary - Component stack:', errorInfo.componentStack);
    console.error('ShadowEmbed Error Boundary - Error:', error);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      const FallbackComponent = this.props.fallback;
      
      if (FallbackComponent) {
        return <FallbackComponent error={this.state.error} />;
      }

      return (
        <div style={{
          padding: '20px',
          border: '2px solid #ff6b6b',
          borderRadius: '8px',
          backgroundColor: '#ffe0e0',
          color: '#d63031'
        }}>
          <h3>ShadowEmbed Error</h3>
          <p><strong>Error:</strong> {this.state.error.message}</p>
          <p><strong>Stack:</strong></p>
          <pre style={{ 
            fontSize: '12px', 
            overflow: 'auto',
            backgroundColor: '#f8f9fa',
            padding: '10px',
            borderRadius: '4px'
          }}>
            {this.state.error.stack}
          </pre>
          <button 
            onClick={() => this.setState({ hasError: false, error: undefined })}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '10px'
            }}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Fallback component untuk error yang lebih user-friendly
export const ShadowEmbedErrorFallback: React.FC<{ error: Error }> = ({ error }) => (
  <div style={{
    padding: '20px',
    border: '2px solid #ffc107',
    borderRadius: '8px',
    backgroundColor: '#fff3cd',
    color: '#856404'
  }}>
    <h4>⚠️ Shadow Embed Loading Failed</h4>
    <p><strong>Issue:</strong> {error.message}</p>
    
    <details style={{ marginTop: '10px' }}>
      <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
        Technical Details
      </summary>
      <pre style={{ 
        fontSize: '11px', 
        marginTop: '10px',
        backgroundColor: '#f8f9fa',
        padding: '8px',
        borderRadius: '4px',
        overflow: 'auto'
      }}>
        {error.stack}
      </pre>
    </details>
    
    <div style={{ marginTop: '15px' }}>
      <p><strong>Possible Solutions:</strong></p>
      <ul style={{ marginLeft: '20px', fontSize: '14px' }}>
        <li>Check if the URL is accessible</li>
        <li>Ensure development server is running</li>
        <li>Verify the HTML file exists in public folder</li>
        <li>Check browser console for additional errors</li>
        <li>Try refreshing the page</li>
      </ul>
    </div>
  </div>
);